import React from 'react'
import PropTypes from 'prop-types'
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	ResponsiveContainer
} from 'recharts'
import './DonutChart.scss'

/**
 * DonutChart - Responsive donut/pie chart
 *
 * @param {Array} data - Data array [{name, value, color}]
 * @param {string} dataKey - Key for data values
 * @param {string} nameKey - Key for data names
 * @param {number} height - Chart height in pixels
 * @param {number} innerRadius - Inner radius (0 for pie, >0 for donut)
 * @param {number} outerRadius - Outer radius
 * @param {boolean} showLegend - Show legend
 * @param {boolean} showTooltip - Show tooltip on hover
 * @param {string} title - Chart title
 * @param {string|React.ReactNode} centerText - Text to display in center
 * @param {string} centerSubtext - Subtext below center text
 * @param {boolean} loading - Show loading state
 */
const DonutChart = ({
	data = [],
	dataKey = 'value',
	nameKey = 'name',
	height = 300,
	innerRadius = 60,
	outerRadius = 100,
	showLegend = true,
	showTooltip = true,
	title,
	centerText,
	centerSubtext,
	loading = false
}) => {
	const defaultColors = [
		'#3b82f6', // blue
		'#10b981', // green
		'#f59e0b', // amber
		'#ef4444', // red
		'#6366f1', // indigo
		'#8b5cf6', // purple
		'#ec4899', // pink
		'#14b8a6'  // teal
	]

	const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0)

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const item = payload[0]
			return (
				<div className="donut-chart__tooltip">
					<p className="donut-chart__tooltip-label" style={{ color: item.payload.color || item.color }}>
						{item.name}
					</p>
					<p className="donut-chart__tooltip-value">
						{item.value} ({((item.value / total) * 100).toFixed(1)}%)
					</p>
				</div>
			)
		}
		return null
	}

	// Calculate chart height (excluding legend space)
	const chartHeight = showLegend ? Math.max(height - 80, 150) : height

	if (loading) {
		return (
			<div className="donut-chart">
				{title && <h3 className="donut-chart__title">{title}</h3>}
				<div className="donut-chart__skeleton" style={{ height }}>
					<div className="skeleton skeleton--chart"></div>
				</div>
			</div>
		)
	}

	if (data.length === 0 || total === 0) {
		return (
			<div className="donut-chart">
				{title && <h3 className="donut-chart__title">{title}</h3>}
				<div className="donut-chart__empty" style={{ height }}>
					<p>No data available</p>
				</div>
			</div>
		)
	}

	return (
		<div className="donut-chart">
			{title && <h3 className="donut-chart__title">{title}</h3>}

			{/* Chart wrapper with center text */}
			<div className="donut-chart__chart-wrapper" style={{ height: chartHeight }}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							cx="50%"
							cy="50%"
							innerRadius={innerRadius}
							outerRadius={outerRadius}
							paddingAngle={2}
							dataKey={dataKey}
							nameKey={nameKey}
						>
							{data.map((entry, index) => (
								<Cell
									key={`cell-${index}`}
									fill={entry.color || defaultColors[index % defaultColors.length]}
								/>
							))}
						</Pie>
						{showTooltip && <Tooltip content={<CustomTooltip />} />}
					</PieChart>
				</ResponsiveContainer>

				{/* Center text overlay */}
				{(centerText || centerSubtext) && innerRadius > 0 && (
					<div className="donut-chart__center">
						{centerText && <span className="donut-chart__center-text">{centerText}</span>}
						{centerSubtext && <span className="donut-chart__center-subtext">{centerSubtext}</span>}
					</div>
				)}
			</div>

			{/* Custom Legend - rendered outside the chart */}
			{showLegend && (
				<ul className="donut-chart__legend">
					{data.map((entry, index) => (
						<li key={`legend-${index}`} className="donut-chart__legend-item">
							<span
								className="donut-chart__legend-color"
								style={{ backgroundColor: entry.color || defaultColors[index % defaultColors.length] }}
							/>
							<span className="donut-chart__legend-text">{entry[nameKey]}</span>
							<span className="donut-chart__legend-value">
								{entry[dataKey]}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

DonutChart.propTypes = {
	data: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			value: PropTypes.number,
			color: PropTypes.string
		})
	),
	dataKey: PropTypes.string,
	nameKey: PropTypes.string,
	height: PropTypes.number,
	innerRadius: PropTypes.number,
	outerRadius: PropTypes.number,
	showLegend: PropTypes.bool,
	showTooltip: PropTypes.bool,
	title: PropTypes.string,
	centerText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	centerSubtext: PropTypes.string,
	loading: PropTypes.bool
}

export default DonutChart
